import React, { useState } from 'react';
import { KeyRound, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  isSixDigitCode,
  useStartTotpEnrolment,
  useVerifyTotp,
  type TotpEnrolment,
} from '@/hooks/useTwoStepVerification';
import { getErrorMessage } from '@/lib/error-utils';

function CodeForm({
  id,
  factorId,
  submitLabel,
  onVerified,
}: {
  id: string;
  factorId: string;
  submitLabel: string;
  onVerified?: () => void;
}) {
  const [code, setCode] = useState('');
  const verify = useVerifyTotp();

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={async e => {
        e.preventDefault();
        if (!isSixDigitCode(code)) return;
        try {
          await verify.mutateAsync({ factorId, code });
          setCode('');
          onVerified?.();
        } catch {
          // Shown inline below.
        }
      }}
    >
      <Label htmlFor={id}>6-digit code from your authenticator app</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={code}
          onChange={e => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          placeholder="123 456"
          className="max-w-40"
        />
        <Button type="submit" disabled={verify.isPending || !isSixDigitCode(code)}>
          {verify.isPending ? 'Checking…' : submitLabel}
        </Button>
      </div>
      {verify.error && (
        <p className="text-sm text-danger" role="alert">
          {getErrorMessage(verify.error, 'That code did not work. Codes change every 30 seconds; try the current one.')}
        </p>
      )}
    </form>
  );
}

/** Asks for a code from an authenticator that is already set up. */
export function TotpCodePanel({ factorId, onVerified }: { factorId: string; onVerified?: () => void }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="flex items-center gap-2 text-sm">
        <Smartphone className="h-4 w-4" aria-hidden="true" />
        Open your authenticator app and enter the current code.
      </p>
      <CodeForm id={`totp-code-${factorId}`} factorId={factorId} submitLabel="Verify" onVerified={onVerified} />
    </div>
  );
}

/** Sets up a new authenticator: scan, then confirm with a code. */
export function TotpEnrolPanel({ onVerified, startLabel = 'Set up two-step verification' }: {
  onVerified?: () => void;
  startLabel?: string;
}) {
  const start = useStartTotpEnrolment();
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);

  if (!enrolment) {
    return (
      <div className="flex flex-col gap-2">
        <Button
          className="self-start"
          disabled={start.isPending}
          onClick={async () => {
            try {
              setEnrolment(await start.mutateAsync());
            } catch {
              // Shown inline below.
            }
          }}
        >
          <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
          {start.isPending ? 'Preparing…' : startLabel}
        </Button>
        {start.error && (
          <p className="text-sm text-danger" role="alert">
            {getErrorMessage(start.error, 'Two-step verification could not be started. Please try again.')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ol className="flex flex-col gap-1 text-sm text-muted-foreground list-decimal pl-5">
        <li>Open an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password.</li>
        <li>Scan this code, or type the setup key.</li>
        <li>Enter the 6-digit code the app shows.</li>
      </ol>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <img
          src={enrolment.qrCode}
          alt="QR code for your authenticator app"
          className="h-40 w-40 rounded-md border border-border bg-background p-2"
        />
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">Setup key</span>
          <code className="break-all rounded bg-muted px-2 py-1 text-sm">{enrolment.secret}</code>
        </div>
      </div>
      <CodeForm
        id={`totp-enrol-${enrolment.factorId}`}
        factorId={enrolment.factorId}
        submitLabel="Turn on"
        onVerified={() => {
          setEnrolment(null);
          onVerified?.();
        }}
      />
    </div>
  );
}
